import { useState, useRef, useEffect } from 'react';
import { makeCall } from '@services/twilioService';
import { initiateIvrCall } from '@services/api';
import { parseApiError, parseIvrBlockedMessage } from '@utils/errorUtils';
import PatientSearch from './PatientSearch';
import './CallControls.css';

const PRODUCT_LINES = ['', 'CPAP', 'CGM'];

export default function OutgoingCall({
  device,
  isReady,
  onCallStarted,
  onCallEnded,
  onCallConnectionReady,
  defaultPatientGuid,
}) {
  const [callMode, setCallMode] = useState('manual'); // 'manual' | 'ivr'
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Manual call state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [patientGuid, setPatientGuid] = useState('');
  const [productLine, setProductLine] = useState('');
  const [autodialer, setAutodialer] = useState(false);
  const [customParams, setCustomParams] = useState('');
  const [customParamsError, setCustomParamsError] = useState('');
  const [callActive, setCallActive] = useState(false);
  const [error, setError] = useState('');
  const callRef = useRef(null);

  // IVR call state
  const [ivrPatientGuid, setIvrPatientGuid] = useState('');
  const [ivrOverrideDailyLimit, setIvrOverrideDailyLimit] = useState(false);
  const [ivrSubmitting, setIvrSubmitting] = useState(false);
  const [ivrError, setIvrError] = useState('');
  const [ivrResult, setIvrResult] = useState(null);

  // Initialize patientGuid (and IVR patient GUID) with default when available
  useEffect(() => {
    if (defaultPatientGuid) {
      if (!patientGuid) setPatientGuid(defaultPatientGuid);
      if (!ivrPatientGuid) setIvrPatientGuid(defaultPatientGuid);
    }
  }, [defaultPatientGuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseCustomParams = () => {
    if (!customParams.trim()) return {};
    try {
      setCustomParamsError('');
      return JSON.parse(customParams);
    } catch (err) {
      setCustomParamsError(`Invalid JSON: ${err.message}`);
      throw err;
    }
  };

  const handleMakeCall = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }
    if (!isReady || !device) {
      setError('Twilio device not ready');
      return;
    }

    try {
      setError('');
      setCustomParamsError('');
      const parsedCustomParams = parseCustomParams();

      const call = await makeCall(phoneNumber, {
        patientGuid: patientGuid || undefined,
        productLine: productLine || undefined,
        autodialer: autodialer || undefined,
        customParams: parsedCustomParams,
      });
      callRef.current = call;
      setCallActive(true);

      if (onCallConnectionReady) onCallConnectionReady(call);

      call.on('disconnect', () => {
        callRef.current = null;
        setCallActive(false);
        if (onCallEnded) onCallEnded();
      });

      call.on('error', (err) => {
        console.error('Call error:', err);
        setError('Call error: ' + err.message);
        callRef.current = null;
        setCallActive(false);
        if (onCallEnded) onCallEnded();
      });

      if (onCallStarted) onCallStarted(phoneNumber);
    } catch (err) {
      setError('Failed to make call: ' + err.message);
    }
  };

  const handleIvrSubmit = async (e) => {
    e.preventDefault();
    setIvrError('');
    setIvrResult(null);

    if (!ivrPatientGuid.trim()) {
      setIvrError('Patient GUID is required.');
      return;
    }

    try {
      setIvrSubmitting(true);
      const data = await initiateIvrCall({
        patientGuid: ivrPatientGuid.trim(),
        overrideDailyLimit: ivrOverrideDailyLimit,
      });
      setIvrResult(data);
    } catch (err) {
      setIvrError(parseIvrBlockedMessage(err) || 'Failed to initiate IVR call: ' + parseApiError(err));
    } finally {
      setIvrSubmitting(false);
    }
  };

  const handleIvrReset = () => {
    setIvrResult(null);
    setIvrError('');
  };

  // Manual tab: selecting a patient fills the phone number and patient GUID.
  const handleManualPatientSelect = (patient) => {
    if (patient.phoneNumber) setPhoneNumber(patient.phoneNumber);
    setPatientGuid(patient.guid);
  };

  // IVR tab: selecting a patient fills the patient GUID (phone is resolved server-side).
  const handleIvrPatientSelect = (patient) => {
    setIvrPatientGuid(patient.guid);
  };

  return (
    <div className="call-control outgoing-call">
      <h2>Make Outgoing Call</h2>

      <div className="call-mode-toggle" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={callMode === 'manual'}
          className={`call-mode-tab ${callMode === 'manual' ? 'active' : ''}`}
          onClick={() => setCallMode('manual')}
        >
          Manual
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={callMode === 'ivr'}
          className={`call-mode-tab ${callMode === 'ivr' ? 'active' : ''}`}
          onClick={() => setCallMode('ivr')}
        >
          IVR Resupply
        </button>
      </div>

      {callMode === 'manual' ? (
        <ManualCallForm
          isReady={isReady}
          callActive={callActive}
          error={error}
          customParamsError={customParamsError}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          patientGuid={patientGuid}
          setPatientGuid={setPatientGuid}
          productLine={productLine}
          setProductLine={setProductLine}
          autodialer={autodialer}
          setAutodialer={setAutodialer}
          customParams={customParams}
          setCustomParams={setCustomParams}
          showAdvanced={showAdvanced}
          setShowAdvanced={setShowAdvanced}
          defaultPatientGuid={defaultPatientGuid}
          handleMakeCall={handleMakeCall}
          onPatientSelect={handleManualPatientSelect}
        />
      ) : (
        <IvrCallForm
          patientGuid={ivrPatientGuid}
          setPatientGuid={setIvrPatientGuid}
          overrideDailyLimit={ivrOverrideDailyLimit}
          setOverrideDailyLimit={setIvrOverrideDailyLimit}
          submitting={ivrSubmitting}
          error={ivrError}
          result={ivrResult}
          onSubmit={handleIvrSubmit}
          onReset={handleIvrReset}
          onPatientSelect={handleIvrPatientSelect}
        />
      )}
    </div>
  );
}

function ManualCallForm({
  isReady, callActive, error, customParamsError,
  phoneNumber, setPhoneNumber,
  patientGuid, setPatientGuid,
  productLine, setProductLine,
  autodialer, setAutodialer,
  customParams, setCustomParams,
  showAdvanced, setShowAdvanced,
  defaultPatientGuid, handleMakeCall, onPatientSelect,
}) {
  if (callActive) {
    return (
      <div className="active-call">
        <div className="call-info">
          <p className="calling-to">Calling: {phoneNumber}</p>
          {patientGuid && <p className="patient-guid">Patient: {patientGuid.substring(0, 8)}...</p>}
          <div className="call-status">Call in progress &mdash; use controls above</div>
        </div>
      </div>
    );
  }

  return (
    <div className="call-form">
      {error && <div className="error-message">{error}</div>}
      {customParamsError && <div className="error-message">JSON Error: {customParamsError}</div>}

      <PatientSearch onSelect={onPatientSelect} disabled={!isReady} />

      <div className="call-input-group">
        <label htmlFor="phoneNumber">Phone Number</label>
        <input
          id="phoneNumber"
          type="tel"
          placeholder="Enter phone number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={!isReady}
        />
      </div>

      <div className="call-input-group">
        <label htmlFor="patientGuid">Patient GUID (optional)</label>
        <input
          id="patientGuid"
          type="text"
          placeholder="Enter patient GUID for testing"
          value={patientGuid}
          onChange={(e) => setPatientGuid(e.target.value)}
          disabled={!isReady}
        />
        {defaultPatientGuid && (
          <small className="input-hint">Default from config: {defaultPatientGuid.substring(0, 8)}...</small>
        )}
      </div>

      <button
        type="button"
        className="advanced-toggle"
        onClick={() => setShowAdvanced((s) => !s)}
        aria-expanded={showAdvanced}
      >
        {showAdvanced ? '▾ Hide advanced options' : '▸ Show advanced options'}
      </button>

      {showAdvanced && (
        <div className="advanced-options">
          <div className="call-input-group">
            <label htmlFor="productLine">Product Line</label>
            <select
              id="productLine"
              value={productLine}
              onChange={(e) => setProductLine(e.target.value)}
              disabled={!isReady}
            >
              {PRODUCT_LINES.map((pl) => (
                <option key={pl} value={pl}>{pl || '-- None --'}</option>
              ))}
            </select>
          </div>

          <div className="call-input-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={autodialer}
                onChange={(e) => setAutodialer(e.target.checked)}
                disabled={!isReady}
              />
              Autodialer Mode
            </label>
          </div>

          <div className="call-input-group">
            <label htmlFor="customParams">Custom Parameters (JSON)</label>
            <textarea
              id="customParams"
              placeholder='{"customKey": "customValue"}'
              value={customParams}
              onChange={(e) => setCustomParams(e.target.value)}
              disabled={!isReady}
              rows="3"
              className="custom-params-textarea"
            />
            <small className="input-hint">Merged with call parameters</small>
          </div>
        </div>
      )}

      <button
        onClick={handleMakeCall}
        disabled={!isReady || !phoneNumber.trim()}
        className="btn-primary"
      >
        Make Call
      </button>
    </div>
  );
}

function IvrCallForm({
  patientGuid, setPatientGuid,
  overrideDailyLimit, setOverrideDailyLimit,
  submitting, error, result, onSubmit, onReset, onPatientSelect,
}) {
  if (result) {
    return (
      <div className="ivr-inline-result">
        <div className="ivr-inline-result-header">
          <span className="ivr-inline-result-icon">✓</span>
          <strong>IVR Call Initiated</strong>
        </div>
        <dl className="ivr-inline-result-grid">
          <dt>Call SID</dt><dd className="mono">{result.callSid}</dd>
          <dt>Status</dt><dd>{result.status}</dd>
          <dt>To</dt><dd className="mono">{result.to}</dd>
          <dt>From</dt><dd className="mono">{result.from}</dd>
        </dl>
        <button onClick={onReset} className="btn-secondary">Start Another</button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="call-form">
      {error && <div className="error-message">{error}</div>}

      <PatientSearch onSelect={onPatientSelect} disabled={submitting} />

      <div className="call-input-group">
        <label htmlFor="ivrPatientGuid">Patient GUID</label>
        <input
          id="ivrPatientGuid"
          type="text"
          placeholder="e.g. 3f2a1b4c-..."
          value={patientGuid}
          onChange={(e) => setPatientGuid(e.target.value)}
          disabled={submitting}
          autoComplete="off"
        />
        <small className="input-hint">
          Patient's primary phone is looked up automatically. Twilio AMD detects human vs. machine; if a machine is detected, voicemail is left.
        </small>
      </div>

      <div className="call-input-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={overrideDailyLimit}
            onChange={(e) => setOverrideDailyLimit(e.target.checked)}
            disabled={submitting}
          />
          Allow repeat call today (testing override)
        </label>
        <small className="input-hint">
          Bypasses the once-per-day call guard so you can re-test the same patient. Honored on dev/qa only — ignored in stage/production.
        </small>
      </div>

      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? 'Initiating...' : 'Start IVR Call'}
      </button>
    </form>
  );
}
